/**
 * Purpose:
 *   Hook to manage individual menu item state and computed styles.
 *   Handles submenu height calculation and animation styles.
 *
 * Notes:
 *   - Calculates submenu height for smooth animations
 *   - Provides computed styles for text, chevron, and container
 *   - Handles collapsed state transitions
 */

'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import type { MenuItemConfig } from '@/types/menu';

// Animation duration - synchronized with Menu component
const ANIMATION_DURATION = 350;
const TRANSITION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

// Fixed icon position from left edge
const ICON_LEFT_OFFSET = 14;

export interface UseMenuItemProps {
    item: MenuItemConfig;
    isExpanded: boolean;
    isActive: boolean;
    isParentActive: boolean;
    isCollapsed: boolean;
}

export interface UseMenuItemReturn {
    /** Whether this item has children */
    hasChildren: boolean;
    /** Reference for submenu height calculation */
    submenuRef: React.RefObject<HTMLDivElement | null>;
    /** Whether to show active styling */
    showActiveState: boolean;
    /** Effective expanded state (respects collapsed mode) */
    effectiveExpanded: boolean;
    /** Styles for the container element */
    containerStyle: React.CSSProperties;
    /** Styles for the text element */
    textStyle: React.CSSProperties;
    /** Styles for the chevron container */
    chevronContainerStyle: React.CSSProperties;
    /** Styles for the submenu */
    submenuStyle: React.CSSProperties;
    /** Styles for the icon container */
    iconContainerStyle: React.CSSProperties;
}

export function useMenuItem({
    item,
    isExpanded,
    isActive,
    isParentActive,
    isCollapsed,
}: UseMenuItemProps): UseMenuItemReturn {
    const hasChildren = Boolean(item.children?.length);
    const submenuRef = useRef<HTMLDivElement>(null);
    const [submenuHeight, setSubmenuHeight] = useState(0);

    // Calculate submenu height for smooth animation
    const updateHeight = useCallback(() => {
        if (submenuRef.current) {
            setSubmenuHeight(submenuRef.current.scrollHeight);
        }
    }, []);

    useEffect(() => {
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [item.children, updateHeight]);

    // Determine if item should show active styling
    const showActiveState = hasChildren ? isParentActive : isActive;

    // When collapsed, submenus should be closed
    const effectiveExpanded = isCollapsed ? false : isExpanded;

    // Transition string
    const transition = `${ANIMATION_DURATION}ms ${TRANSITION_EASING}`;

    // Container style - FIXED padding, icon position never changes
    const containerStyle: React.CSSProperties = {
        paddingLeft: `${ICON_LEFT_OFFSET}px`,
        paddingRight: isCollapsed ? `${ICON_LEFT_OFFSET}px` : '16px',
        gap: '12px',
        transition: `padding ${transition}`,
    };

    // Text style - fades out and shrinks
    const textStyle: React.CSSProperties = {
        opacity: isCollapsed ? 0 : 1,
        maxWidth: isCollapsed ? 0 : '200px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        transition: `opacity ${transition}, max-width ${transition}`,
    };

    // Chevron container style
    const chevronContainerStyle: React.CSSProperties = {
        opacity: isCollapsed ? 0 : 1,
        maxWidth: isCollapsed ? 0 : '24px',
        overflow: 'hidden',
        marginLeft: 'auto',
        flexShrink: 0,
        transition: `opacity ${transition}, max-width ${transition}`,
    };

    // Submenu style
    const submenuStyle: React.CSSProperties = {
        height: effectiveExpanded ? submenuHeight : 0,
        overflow: 'hidden',
        transition: `height ${transition}`,
    };

    // Icon container - fixed size, never changes
    const iconContainerStyle: React.CSSProperties = {
        flexShrink: 0,
        width: '20px',
        height: '20px',
    };

    return {
        hasChildren,
        submenuRef,
        showActiveState,
        effectiveExpanded,
        containerStyle,
        textStyle,
        chevronContainerStyle,
        submenuStyle,
        iconContainerStyle,
    };
}

